import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  Descriptions,
  Image,
  Tag,
  Divider,
  List,
  Collapse,
  Button,
  Dropdown,
  Space,
  Typography,
  Row,
  Col,
  Avatar,
  Table,
  Form,
  Input,
  Select,
  Empty,
  Modal,
  DatePicker,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  HomeOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
  MoreOutlined,
  DeleteOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../firebase";
import {
  GreenNotify,
  RedNotify,
  emailFormat,
  splitEmails,
} from "../../helper/helper";
import { statusColor, statusLabel } from "../../helper/statusMeta";
import { inspectorIds } from "../../helper/inspections";
import { createPropertyTask } from "../../helper/propertyTasks";
import {
  getDisplayName,
  getUserLabel,
  isAssignableInspector,
} from "../../helper/userDisplay";
import AssignInspectors from "../../components/AssignInspectors/AssignInspectors";
import Loader from "../../components/loader/loader";
import PropertyFormModal from "../promoterList/PropertyFormModal";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../helper/googleMaps";
import { normalizeCoordinates } from "../../helper/coordinates";

const MAP_CONTAINER_STYLE = { width: "100%", height: 260, borderRadius: 8 };

const { Text } = Typography;
const { Panel } = Collapse;

// A PDF is only meaningful for a completed inspection — never trigger a
// server-side render for an incomplete one.
const isCompletedInspection = (insp) =>
  String(insp?.status || "")
    .toLowerCase()
    .includes("complet");

const TaskDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  // Property edit now uses the shared PropertyFormModal (same as the dashboard).
  const [editOpen, setEditOpen] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Manual "Send report to owner": which inspection, plus the editable
  // recipient/cc form and an in-flight flag.
  const [sendInspection, setSendInspection] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendForm] = Form.useForm();
  // Notes log (lives on the property doc as `propertyNotes[]`).
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Create-task modal (writes to the property_tasks collection).
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskForm] = Form.useForm();
  // Inspection detail modal (opened from the history "Review" action).
  const [reviewInspection, setReviewInspection] = useState(null);

  // Logged-in admin, used to attribute notes/tasks.
  const adminUser = useSelector((s) => s.userDataSlice?.userData);
  const currentName = getDisplayName(adminUser);

  // Shared singleton Maps loader (same options everywhere) for the location map.
  const { isLoaded: isMapsLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const coords = normalizeCoordinates(property?.coordinates);

  const handleDownloadPdf = async (inspection) => {
    const inspectionId = inspection?.id || inspection?.docId || inspection?._id;
    if (!inspectionId) {
      RedNotify("No inspection to download yet.");
      return;
    }
    try {
      setDownloadingPdf(true);
      // Render the report server-side (same renderer as the owner email) and
      // download the returned PDF — keeps staff + owner copies identical.
      const generate = httpsCallable(functions, "generateInspectionReport");
      const res = await generate({ inspectionId });
      const { pdfBase64, filename } = res.data || {};
      if (!pdfBase64) throw new Error("Empty report");
      const blob = await (
        await fetch(`data:application/pdf;base64,${pdfBase64}`)
      ).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "inspection-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation error:", error);
      RedNotify(error?.message || "Failed to generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Open the "Send report to owner" modal; the form is prefilled in an effect
  // once it's mounted (see below).
  const openSendReport = (inspection) => {
    const inspectionId = inspection?.id || inspection?.docId || inspection?._id;
    if (!inspectionId) {
      RedNotify("No inspection to send yet.");
      return;
    }
    setSendInspection(inspection);
  };

  // Queue the owner-report email server-side (same renderer as the auto-send),
  // to the recipient(s) in the form. Lets staff (re)send a report on demand —
  // e.g. after correcting an owner email.
  const handleSendReport = async () => {
    let values;
    try {
      values = await sendForm.validateFields();
    } catch {
      return; // inline validation errors
    }
    const inspection = sendInspection;
    const inspectionId =
      inspection?.id || inspection?.docId || inspection?._id;
    if (!inspectionId) return;
    const to = splitEmails(values.to);
    const cc = splitEmails(values.cc);
    try {
      setSendingReport(true);
      const send = httpsCallable(functions, "sendOwnerReport");
      await send({ inspectionId, to, cc });
      GreenNotify(`Report sent to ${to.join(", ")}`);
      setSendInspection(null);
    } catch (error) {
      console.error("send report error:", error);
      RedNotify(error?.message || "Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  // Prefill the send form once the modal (and its Form) is mounted — a
  // synchronous setFieldsValue in the click handler would run before
  // destroyOnClose remounts the fields and get dropped.
  useEffect(() => {
    if (sendInspection) {
      sendForm.setFieldsValue({
        to: property?.ownerDetails?.email || "",
        cc: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendInspection]);

  const fetchAllUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const users = snapshot.docs.map((doc) => ({
        _id: doc.id,
        ...doc.data(),
      }));
      // keep ALL users for inspector name/email lookup (don't filter by profile state)
      setUsersData(users);
    } catch (error) {
      RedNotify(error?.message);
      console.error("Error fetching users:", error);
    }
  };

  const userName = (uid) => {
    if (!uid) return "Unassigned";
    const u = usersData.find((user) => user._id === uid);
    // name -> email -> id (shared fallback). Users that no longer exist show
    // "Unknown inspector" since we have no record to fall back to.
    return u ? getUserLabel(u) : "Unknown inspector";
  };

  const assignableInspectors = usersData.filter(isAssignableInspector);

  // Persist a new assigned-inspector set on the property (the Cloud Functions
  // mirror it onto open inspections + notify anyone newly added).
  const saveInspectors = async (assignedInspectors) => {
    await updateDoc(doc(db, "properties", id), { assignedInspectors });
    setProperty((p) => (p ? { ...p, assignedInspectors } : p));
  };

  // Notes are an append-only log on the property doc. Add/delete go through
  // arrayUnion/arrayRemove so two admins editing at once don't clobber each
  // other (a whole-array rewrite would last-write-wins).
  const propertyNotes = Array.isArray(property?.propertyNotes)
    ? property.propertyNotes
    : [];
  // Newest first for display, regardless of stored order (arrayUnion appends).
  const sortedNotes = [...propertyNotes].sort((a, b) =>
    (b?.createdAt || "").localeCompare(a?.createdAt || ""),
  );

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      // Collision-proof id: two notes added in the same millisecond must not
      // share an id, or deleting one (arrayRemove) could match the wrong one.
      const noteId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const entry = {
        id: noteId,
        text,
        authorId: auth.currentUser?.uid || null,
        authorName: currentName,
        createdAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, "properties", id), {
        propertyNotes: arrayUnion(entry),
      });
      setProperty((p) =>
        p ? { ...p, propertyNotes: [...(p.propertyNotes || []), entry] } : p,
      );
      setNoteText("");
    } catch (error) {
      console.error("add note failed", error);
      RedNotify("Couldn't save note");
    } finally {
      setSavingNote(false);
    }
  };

  // `note` is the exact stored object (we render straight from the doc), so
  // arrayRemove matches it by value — concurrency-safe vs a full rewrite.
  const handleDeleteNote = async (note) => {
    try {
      await updateDoc(doc(db, "properties", id), {
        propertyNotes: arrayRemove(note),
      });
      setProperty((p) =>
        p
          ? {
              ...p,
              propertyNotes: (p.propertyNotes || []).filter(
                (n) => n.id !== note.id,
              ),
            }
          : p,
      );
    } catch (error) {
      console.error("delete note failed", error);
      RedNotify("Couldn't delete note");
    }
  };

  // Create a property_task scoped to this property (mirrors the Property
  // Tasks page so the same doc shape / rules apply).
  const handleCreateTask = async (values) => {
    setCreatingTask(true);
    try {
      await createPropertyTask({
        title: values.title,
        description: values.description,
        propertyId: id,
        propertyName: property?.propertyName,
        deadline: values.deadline,
        assignedTo: values.assignedTo,
        users: usersData,
        createdByName: currentName,
      });
      GreenNotify("Task created");
      setTaskModalOpen(false);
      taskForm.resetFields();
    } catch (error) {
      console.error("create task failed", error);
      RedNotify("Couldn't create task");
    } finally {
      setCreatingTask(false);
    }
  };

  const fetchPropertyById = async (uuid) => {
    try {
      const docRef = doc(db, "properties", uuid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setProperty({ _id: docSnap.id, ...docSnap.data() });
      } else {
        console.log("No such property!");
        RedNotify("No such property!");
      }
    } catch (error) {
      RedNotify(error?.message);
      console.log("Error fetching property by ID:", error);
    }
  };

  const fetchInspections = async (propertyId) => {
    try {
      const q = query(
        collection(db, "inspections"),
        where("propertyId", "==", propertyId),
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs
        .map((d) => ({ _id: d.id, ...d.data() }))
        // order by createdAt desc client-side (avoids needing a composite index)
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      setInspections(list);
      setSelectedInspection(list[0] || null);
    } catch (error) {
      console.error("Error fetching inspections:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchPropertyById(id),
        fetchInspections(id),
        fetchAllUsers(),
      ]);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const inspectionColumns = [
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        date ? dayjs(date).format("DD MMM YYYY, HH:mm") : "N/A",
    },
    {
      title: "Inspector",
      key: "inspectors",
      render: (_, r) => {
        const names = inspectorIds(r).map(userName).filter(Boolean);
        return names.length ? names.join(", ") : "Unassigned";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColor(status)}>{statusLabel(status)}</Tag>
      ),
    },
    {
      title: "Completed",
      dataIndex: "completedAt",
      key: "completedAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "review",
                label: "Review",
                onClick: () => {
                  setSelectedInspection(record);
                  setReviewInspection(record);
                },
              },
              {
                key: "pdf",
                icon: <DownloadOutlined />,
                label: isCompletedInspection(record)
                  ? "Download PDF"
                  : "Download PDF (not completed)",
                disabled: downloadingPdf || !isCompletedInspection(record),
                onClick: () => handleDownloadPdf(record),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (loading) {
    return <Loader loading={loading} />;
  }

  if (!property) {
    return <div>Property not found</div>;
  }

  // An area counts as "completed" once it has checks and every check is
  // answered (replaces the dropped per-item isChecked flag).
  const areaAnswered = (a) =>
    Array.isArray(a?.checks) &&
    a.checks.length > 0 &&
    a.checks.every((c) => c?.answer === "yes" || c?.answer === "no");

  const reviewChecklist = selectedInspection?.questionaire?.length
    ? selectedInspection.questionaire
    : property.checklistTemplate || [];

  // Completed inspections power the header's "Download PDF" picker.
  const completedInspections = inspections.filter(isCompletedInspection);

  // Shared renderer for an inspection's checklist answers — used by the sidebar
  // "Inspection Answers" card and the Review (inspection detail) modal.
  const renderChecklistCollapse = (items) => (
    <Collapse accordion>
      {(items || []).map((room, index) => (
        <Panel
          header={
            <Space>
              <HomeOutlined />
              <Text strong>{room.area}</Text>
              <Tag color={areaAnswered(room) ? "green" : "orange"}>
                {areaAnswered(room) ? "Completed" : "Pending"}
              </Tag>
            </Space>
          }
          key={index}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            {room?.image && (
              <Image
                src={room?.image}
                style={{
                  borderRadius: 4,
                  maxHeight: 200,
                  objectFit: "cover",
                  width: "100%",
                  marginBottom: "10px",
                }}
              />
            )}
            <Text strong>Question: {room.question}</Text>
            {room?.note ? (
              <Text type="secondary">Note: {room.note}</Text>
            ) : null}
          </div>
          <Divider style={{ margin: "12px 0" }} />
          <List
            dataSource={room.checks}
            renderItem={(problem) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      size="small"
                      icon={<CheckCircleOutlined />}
                      style={{
                        backgroundColor:
                          problem.answer === "no"
                            ? "#c2603d"
                            : problem.answer === "yes"
                              ? "#52c41a"
                              : "#f0f0f0",
                        color: problem.answer ? "#fff" : "#999",
                      }}
                    />
                  }
                  title={
                    <Text>
                      {problem.label}
                      {problem.answer
                        ? ` — ${problem.answer === "no" ? "No" : "Yes"}`
                        : ""}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
          {(typeof room?.resolved === "boolean" ||
            (room?.stepsCompleted && room.stepsCompleted.length > 0) ||
            (room?.resolutionSteps && room.resolutionSteps.length > 0)) && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space>
                  <Text strong>Resolution:</Text>
                  {room?.resolved === true && (
                    <Tag color="green">Resolved on-site</Tag>
                  )}
                  {room?.resolved === false && (
                    <Tag color="red">Issue persists</Tag>
                  )}
                </Space>
                {room?.stepsCompleted && room.stepsCompleted.length > 0 && (
                  <List
                    size="small"
                    header={<Text type="secondary">Steps completed</Text>}
                    dataSource={room.stepsCompleted}
                    renderItem={(step) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              size="small"
                              icon={<CheckCircleOutlined />}
                              style={{
                                backgroundColor: "#52c41a",
                                color: "#fff",
                              }}
                            />
                          }
                          title={<Text>{step}</Text>}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Space>
            </>
          )}
        </Panel>
      ))}
    </Collapse>
  );

  return (
    <div className="sh-detail-page">
      <Card
        title={
          <Space wrap>
            <div>
              <Descriptions.Item label="Property Name">
                {property.propertyName}
              </Descriptions.Item>
              {selectedInspection && (
                <Tag
                  style={{ marginLeft: "5px" }}
                  color={statusColor(selectedInspection.status)}
                >
                  {statusLabel(selectedInspection.status).toUpperCase()}
                </Tag>
              )}
            </div>
          </Space>
        }
        extra={
          <Space wrap>
            <Tooltip
              title={
                completedInspections.length === 0
                  ? "No completed inspections to download yet"
                  : ""
              }
            >
              <Dropdown
                trigger={["click"]}
                disabled={completedInspections.length === 0}
                menu={{
                  items: completedInspections.map((insp) => {
                    const names = inspectorIds(insp)
                      .map(userName)
                      .filter(Boolean);
                    return {
                      key: insp._id,
                      icon: <DownloadOutlined />,
                      label: `${dayjs(insp.completedAt || insp.createdAt).format(
                        "DD MMM YYYY",
                      )}${names.length ? ` · ${names.join(", ")}` : ""}`,
                      onClick: () => handleDownloadPdf(insp),
                    };
                  }),
                }}
              >
                <Button icon={<DownloadOutlined />} loading={downloadingPdf}>
                  Download PDF
                </Button>
              </Dropdown>
            </Tooltip>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setTaskModalOpen(true)}
            >
              Create Task
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditOpen(true)}
            >
              Edit Property
            </Button>
          </Space>
        }
      >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {/* At-a-glance counts — a compact responsive row instead of one
                  full-width line each. */}
              <Descriptions
                bordered
                size="small"
                column={{ xs: 2, sm: 3, md: 5 }}
              >
                <Descriptions.Item label="Bedrooms">
                  {property.propertyObjects?.beds ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Bathrooms">
                  {property.propertyObjects?.bath ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Kitchens">
                  {property.propertyObjects?.kitchen ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Porches">
                  {property.propertyObjects?.porch ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Lawns">
                  {property.propertyObjects?.lawn ?? "—"}
                </Descriptions.Item>
              </Descriptions>

              {/* Scheduling facts */}
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, md: 2 }}
                style={{ marginTop: 12 }}
                labelStyle={{ width: 150, whiteSpace: "nowrap" }}
              >
                <Descriptions.Item label="Inspection Frequency">
                  {(() => {
                    const w =
                      property.inspectionIntervalWeeks ||
                      (property.inspectionIntervalDays
                        ? Math.max(1, Math.round(property.inspectionIntervalDays / 7))
                        : null);
                    if (!w) return "N/A";
                    return w === 1
                      ? "Weekly"
                      : w === 4
                        ? "Monthly"
                        : `Every ${w} weeks`;
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Last Inspection">
                  {property.lastInspectionDate
                    ? dayjs(property.lastInspectionDate).format("DD MMM YYYY")
                    : "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Assigned Inspectors" span={2}>
                  <AssignInspectors
                    value={
                      Array.isArray(property.assignedInspectors)
                        ? property.assignedInspectors
                        : []
                    }
                    options={assignableInspectors}
                    onSave={saveInspectors}
                    style={{ minWidth: 260 }}
                  />
                </Descriptions.Item>
              </Descriptions>

              {/* Inspection history — surfaced near the top; it's the point of
                  the page, not an afterthought at the bottom. */}
              <Divider orientation="left">Inspection History</Divider>
              {inspections.length === 0 ? (
                <Empty description="No inspections have been assigned for this property yet." />
              ) : (
                <Table
                  columns={inspectionColumns}
                  dataSource={inspections}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  onRow={(record) => ({
                    style: { cursor: "pointer" },
                    onClick: (e) => {
                      // Row click opens Review (same as the ⋯ action) — but let
                      // the actions menu handle its own clicks.
                      if (e.target.closest("button, a, .ant-dropdown-trigger")) {
                        return;
                      }
                      setSelectedInspection(record);
                      setReviewInspection(record);
                    },
                  })}
                  rowClassName={(record) =>
                    record._id === selectedInspection?._id
                      ? "ant-table-row-selected"
                      : ""
                  }
                />
              )}

              <Divider orientation="left">Owner Details</Divider>
              <Descriptions bordered column={1}>
                <Descriptions.Item
                  label={
                    <Space>
                      <UserOutlined />
                      Name
                    </Space>
                  }
                >
                  {property.ownerDetails?.ownerName || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Space>
                      <MailOutlined />
                      Email
                    </Space>
                  }
                >
                  {property.ownerDetails?.email || "N/A"}
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <Space>
                      <PhoneOutlined />
                      Contact
                    </Space>
                  }
                >
                  {property.ownerDetails?.contact || "N/A"}
                </Descriptions.Item>
              </Descriptions>

              <Divider orientation="left">Property Description</Divider>
              <Text>{property.propertyDescription}</Text>

              <Divider orientation="left">Location</Divider>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  coords
                    ? `${coords.lat},${coords.lng}`
                    : property.location || "",
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text style={{ textDecorationLine: "underline" }}>
                  {property.location || "Open in Google Maps"}
                </Text>
              </a>
              {coords && isMapsLoaded && (
                <div style={{ marginTop: 12 }}>
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={coords}
                    zoom={15}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      gestureHandling: "cooperative",
                    }}
                  >
                    <Marker position={coords} title={property.propertyName} />
                  </GoogleMap>
                </div>
              )}
              {coords && !isMapsLoaded && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">Loading map…</Text>
                </div>
              )}

              <Divider orientation="left">Notes</Divider>
              <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
                <Input.TextArea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this property…"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onPressEnter={(e) => {
                    // Enter sends, Shift+Enter for a newline.
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleAddNote();
                    }
                  }}
                />
                <Button
                  type="primary"
                  onClick={handleAddNote}
                  loading={savingNote}
                  disabled={!noteText.trim()}
                >
                  Add Note
                </Button>
              </Space.Compact>
              {propertyNotes.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No notes yet."
                />
              ) : (
                <List
                  size="small"
                  dataSource={sortedNotes}
                  renderItem={(note) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key="delete"
                          title="Delete this note?"
                          okText="Delete"
                          okType="danger"
                          cancelText="Cancel"
                          onConfirm={() => handleDeleteNote(note)}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Text style={{ whiteSpace: "pre-wrap" }}>
                            {note.text}
                          </Text>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {note.authorName || "Unknown"}
                            {note.createdAt
                              ? ` · ${dayjs(note.createdAt).format(
                                  "DD MMM YYYY, HH:mm",
                                )}`
                              : ""}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Col>

            <Col xs={24} lg={8}>
              <Card title="Property Images" style={{ marginBottom: 16 }}>
                <Image.PreviewGroup>
                  <Row gutter={[8, 8]}>
                    {property.propertyImages?.map((img, index) => (
                      <Col span={12} key={index}>
                        <Image
                          src={img}
                          alt={`Property ${index + 1}`}
                          style={{ borderRadius: 4 }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Image.PreviewGroup>
              </Card>

              <Card
                title={
                  selectedInspection
                    ? `Inspection Answers (${dayjs(
                        selectedInspection.createdAt,
                      ).format("DD MMM YYYY")})`
                    : "Checklist Template"
                }
              >
              {renderChecklistCollapse(reviewChecklist)}
              </Card>
            </Col>
          </Row>
      </Card>

      <Modal
        title="New Task"
        open={taskModalOpen}
        onCancel={() => setTaskModalOpen(false)}
        onOk={() => taskForm.submit()}
        confirmLoading={creatingTask}
        okText="Create"
        destroyOnClose
      >
        <p style={{ color: "#888", marginTop: 0 }}>
          {property?.propertyName || "—"}
        </p>
        <Form form={taskForm} layout="vertical" onFinish={handleCreateTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Give the task a title" }]}
          >
            <Input placeholder="e.g. Refill water softener salt" />
          </Form.Item>
          <Form.Item name="description" label="Details (optional)">
            <Input.TextArea
              rows={3}
              placeholder="Anything the inspector should know"
            />
          </Form.Item>
          <Form.Item
            name="assignedTo"
            label="Assign to (optional)"
            extra="Pick one or more inspectors. Leave blank and the admins are notified to assign it."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign to inspectors"
              options={assignableInspectors.map((u) => ({
                value: u._id,
                label: getUserLabel(u),
              }))}
            />
          </Form.Item>
          <Form.Item name="deadline" label="Deadline (optional)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Shared property editor — same modal the dashboard uses. */}
      <PropertyFormModal
        open={editOpen}
        editingTask={property}
        usersData={usersData}
        onClose={() => setEditOpen(false)}
        onSaved={() => fetchPropertyById(id)}
      />

      {/* Inspection detail — opened from the history "Review" action. */}
      <Modal
        title={
          reviewInspection
            ? `Inspection — ${dayjs(reviewInspection.createdAt).format(
                "DD MMM YYYY",
              )}`
            : "Inspection"
        }
        open={!!reviewInspection}
        onCancel={() => setReviewInspection(null)}
        width={760}
        footer={[
          <Tooltip
            key="pdf"
            title={
              isCompletedInspection(reviewInspection)
                ? ""
                : "Available once the inspection is completed"
            }
          >
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingPdf}
              disabled={!isCompletedInspection(reviewInspection)}
              onClick={() => handleDownloadPdf(reviewInspection)}
            >
              Download PDF
            </Button>
          </Tooltip>,
          <Tooltip
            key="send"
            title={
              isCompletedInspection(reviewInspection)
                ? "Email this report to the owner"
                : "Available once the inspection is completed"
            }
          >
            <Button
              icon={<SendOutlined />}
              disabled={!isCompletedInspection(reviewInspection)}
              onClick={() => openSendReport(reviewInspection)}
            >
              Send to owner
            </Button>
          </Tooltip>,
          <Button key="close" onClick={() => setReviewInspection(null)}>
            Close
          </Button>,
        ]}
      >
        {reviewInspection && (
          <>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Status">
                <Tag color={statusColor(reviewInspection.status)}>
                  {statusLabel(reviewInspection.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Inspector(s)">
                {inspectorIds(reviewInspection)
                  .map(userName)
                  .filter(Boolean)
                  .join(", ") || "Unassigned"}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {reviewInspection.createdAt
                  ? dayjs(reviewInspection.createdAt).format("DD MMM YYYY, HH:mm")
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Completed">
                {reviewInspection.completedAt
                  ? dayjs(reviewInspection.completedAt).format(
                      "DD MMM YYYY, HH:mm",
                    )
                  : "—"}
              </Descriptions.Item>
              {reviewInspection.windowStart && (
                <Descriptions.Item label="Window starts">
                  {dayjs(reviewInspection.windowStart).format("DD MMM YYYY")}
                </Descriptions.Item>
              )}
              {reviewInspection.deadline && (
                <Descriptions.Item label="Due by">
                  {dayjs(reviewInspection.deadline).format("DD MMM YYYY")}
                </Descriptions.Item>
              )}
              {reviewInspection.thorough && (
                <Descriptions.Item label="Type" span={2}>
                  <Tag color="warning">★ Thorough (owner arrival)</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
            {renderChecklistCollapse(
              reviewInspection.questionaire?.length
                ? reviewInspection.questionaire
                : property.checklistTemplate,
            )}
          </>
        )}
      </Modal>

      <Modal
        title="Send report to owner"
        open={!!sendInspection}
        onCancel={() => setSendInspection(null)}
        onOk={handleSendReport}
        okText="Send"
        confirmLoading={sendingReport}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: "#888" }}>
          Render this inspection's report and email it. The owner email is
          prefilled — edit the recipient or add a CC if needed.
        </p>
        <Form form={sendForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Recipient"
            name="to"
            rules={[
              { required: true, message: "Enter a recipient email." },
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (list.length && list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Enter a valid email."));
                },
              }),
            ]}
          >
            <Input placeholder="owner@example.com" />
          </Form.Item>
          <Form.Item
            label="CC (optional)"
            name="cc"
            extra="Comma-separate multiple addresses."
            rules={[
              () => ({
                validator(_, value) {
                  const list = splitEmails(value);
                  if (!list.length || list.every((e) => emailFormat.test(e))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("One of the CC addresses looks invalid."),
                  );
                },
              }),
            ]}
          >
            <Input placeholder="office@2ndhomeut.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskDetails;
